import { Modal, PrimaryButton, Icon, Text } from "@artprov/design-system";
import { useModalResultado } from "../hooks/useModalResultado.js";
import PropTypes from "prop-types";
import ProgressBar from "../../../components/ProgressBar";

/**
 * Renderiza el resumen de estadísticas
 * @param {Object} props
 * @param {number} props.total - Total de registros
 * @param {number} props.exitosos - Registros exitosos
 * @param {number} props.fallidos - Registros fallidos
 */
function StatsSummary({ total, exitosos, fallidos }) {
    return (
        <div className="bg-tertiary-95 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col items-center">
                    <Text variant="h5" className="text-tertiary-10">
                        {total}
                    </Text>
                    <Text variant="caption" className="text-tertiary-50">
                        Total
                    </Text>
                </div>
                <div className="flex flex-col items-center">
                    <Text variant="h5" className="text-success-20">
                        {exitosos}
                    </Text>
                    <Text variant="caption" className="text-tertiary-50">
                        Exitosos
                    </Text>
                </div>
                <div className="flex flex-col items-center">
                    <Text variant="h5" className="text-error-20">
                        {fallidos}
                    </Text>
                    <Text variant="caption" className="text-tertiary-50">
                        Fallidos
                    </Text>
                </div>
            </div>
        </div>
    );
}

StatsSummary.propTypes = {
    total: PropTypes.number.isRequired,
    exitosos: PropTypes.number.isRequired,
    fallidos: PropTypes.number.isRequired,
};

/**
 * Renderiza el mensaje de estado
 * @param {Object} props
 * @param {boolean} props.todosExitosos - Indica si todos fueron exitosos
 * @param {number} props.exitosos - Cantidad de exitosos
 * @param {number} props.total - Total de registros
 * @param {number} props.fallidos - Cantidad de fallidos
 */
function StatusMessage({ todosExitosos, exitosos, total, fallidos }) {

    const getMessage = () => {
        if (todosExitosos) {
            return "Todos los registros fueron confirmados exitosamente.";
        }
        const pluralRegistros = fallidos > 1 ? 's' : '';
        const pluralContienen = fallidos > 1 ? 'n' : '';
        const pluralErrores = fallidos > 1 ? 'es' : '';
        return `Se confirmaron ${exitosos} de ${total} registros.\n${fallidos} registro${pluralRegistros} contiene${pluralContienen} error${pluralErrores}.`;
    };

    return (
        <div className={`p-3 rounded-lg flex justify-center w-full`}>
            <Text variant="body2" className="text-tertiary-10 whitespace-pre-line text-center">
                {getMessage()}
            </Text>
        </div>
    );
}

StatusMessage.propTypes = {
    todosExitosos: PropTypes.bool.isRequired,
    exitosos: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    fallidos: PropTypes.number.isRequired,
};

/**
 * Renderiza un item de error individual
 * @param {Object} props
 * @param {Object} props.error - Datos del error
 * @param {number} props.index - Índice para key de respaldo
 */
function ErrorItem({ error, index }) {
    return (
        <div
            key={error.id || index}
            className="p-3 border-t border-tertiary-80 flex items-start gap-2"
        >
            <div className="flex-1 min-w-0">
                <Text variant="caption" className="text-tertiary-50">
                    ID: {error.id}
                </Text>
                <Text
                    variant="body2"
                    className="text-tertiary-30 text-center"
                >
                    {error.mensaje}
                </Text>
            </div>
        </div>
    );
}

ErrorItem.propTypes = {
    error: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        mensaje: PropTypes.string,
    }).isRequired,
    index: PropTypes.number.isRequired,
};

/**
 * Renderiza la sección de errores colapsable
 * @param {Object} props
 * @param {Array} props.errores - Lista de errores
 * @param {boolean} props.isExpanded - Estado de expansión
 * @param {Function} props.toggleExpand - Handler para expandir/colapsar
 */
function ErrorDetailsSection({ errores, isExpanded, toggleExpand }) {
    return (
        <div className="border border-tertiary-80 rounded-lg overflow-hidden">
            <button
                onClick={toggleExpand}
                className="w-full flex items-center justify-between p-3 bg-tertiary-95 hover:bg-tertiary-90 transition-colors"
            >
                <div className="flex items-center gap-2 pl-3">
                    <Text variant="body2" className="text-tertiary-10 font-medium">
                        Ver detalle de errores ({errores.length})
                    </Text>
                </div>
                <Icon
                    icon="expand_more"
                    size="small"
                    className={`text-tertiary-50 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                />
            </button>

            {isExpanded && (
                <div className="max-h-40 overflow-y-auto">
                    {errores.map((error, index) => (
                        <ErrorItem key={error.id || index} error={error} index={index} />
                    ))}
                </div>
            )}
        </div>
    );
}

ErrorDetailsSection.propTypes = {
    errores: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            mensaje: PropTypes.string,
        })
    ).isRequired,
    isExpanded: PropTypes.bool.isRequired,
    toggleExpand: PropTypes.func.isRequired,
};

/**
 * Modal para mostrar los resultados del procesamiento de confirmación
 * Muestra un resumen con estadísticas: total, exitosos, fallidos y detalle de errores
 *
 * @param {Object} props
 * @param {boolean} props.open - Estado de apertura del modal
 * @param {Function} props.setOpen - Función para cambiar el estado de apertura
 * @param {Object} props.resultData - Datos de la respuesta de la API
 * @param {Function} props.onClose - Callback al cerrar el modal
 */
export default function ModalResultado({ open, setOpen, resultData, onClose }) {
    const { isExpanded, toggleExpand, handleClose, handleAccept } = useModalResultado({
        onClose,
        onAccept: () => {
            setOpen(false);
            onClose?.();
        },
    });

    if (!resultData) return null;

    const { total, exitosos, fallidos, porcentaje_exito, detalle } = resultData;
    const todosExitosos = fallidos === 0;
    const hayErrores = fallidos > 0;
    const errores = detalle?.filter((item) => item.status === "error") || [];

    return (
        <Modal.Container
            open={open}
            setOpen={setOpen}
            closeOnClickOutside={false}
            onClose={handleClose}
        >
            <Modal.Content height="auto" width="480px">
                <Modal.Header className="mb-2">
                    <div className="flex items-center gap-2">
                        <Modal.Title className="pl-3">Resultado de la confirmación</Modal.Title>
                    </div>
                    <Modal.Close onClose={handleClose} />
                </Modal.Header>

                <Modal.Body classNameScroll="!w-full" classNameScrollContainer="!overflow-visible">
                    <div className="flex flex-col space-y-4 w-full">
                        <StatsSummary total={total} exitosos={exitosos} fallidos={fallidos} />
                        <ProgressBar porcentaje={porcentaje_exito} />
                        <StatusMessage
                            todosExitosos={todosExitosos}
                            exitosos={exitosos}
                            total={total}
                            fallidos={fallidos}
                        />
                        {hayErrores && errores.length > 0 && (
                            <ErrorDetailsSection
                                errores={errores}
                                isExpanded={isExpanded}
                                toggleExpand={toggleExpand}
                            />
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer className="flex justify-end">
                    <PrimaryButton
                        size="small"
                        onClick={handleAccept}
                        classNameIcon="text-tertiary-100"
                    >
                        Entendido
                    </PrimaryButton>
                </Modal.Footer>
            </Modal.Content>
        </Modal.Container>
    );
}

ModalResultado.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    resultData: PropTypes.shape({
        total: PropTypes.number.isRequired,
        exitosos: PropTypes.number.isRequired,
        fallidos: PropTypes.number.isRequired,
        porcentaje_exito: PropTypes.number.isRequired,
        detalle: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                mensaje: PropTypes.string,
                status: PropTypes.string,
            })
        ),
    }).isRequired,
    onClose: PropTypes.func,
};
